// GovCon knowledge-graph panel on the capture Overview tab.
//
// The tenant entity graph (company / gov_org / vehicle / partner / person nodes
// and their provenance-bearing edges) rendered as a visual reactflow canvas.
// Create a node from the toolbar; draw a relationship by dragging between two
// nodes; click a node or edge to act on it. The §7 entity-resolution machinery
// — competing edges, pin-canonical (override), merge/unmerge, the superseded
// trail, and resolution history — is real but lives in a collapsed "Advanced"
// section so the everyday view stays proposal-person-simple. Data flows through
// the same graph hooks as before.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraIconButton, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronDown, ChevronRight, Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { GraphCanvas } from './GraphCanvas';
import { useDeleteEntityEdge, useDeleteEntityNode, useEntityGraph, useGovOrgChart, useMergeEntityNodes, useOverrideEntityEdge, useResolutionHistory, useUnmergeEntityNodes, useUpsertEntityEdge, useUpsertEntityNode } from './graphHooks';
const NODE_TYPES = [
    {
        value: 'company',
        label: 'Company'
    },
    {
        value: 'gov_org',
        label: 'Government org'
    },
    {
        value: 'vehicle',
        label: 'Vehicle'
    },
    {
        value: 'partner',
        label: 'Partner'
    },
    {
        value: 'person',
        label: 'Person'
    }
];
function nodeTypeLabel(t) {
    return NODE_TYPES.find((n)=>n.value === t)?.label ?? t;
}
function clamp01(n) {
    if (Number.isNaN(n)) return 0;
    return Math.min(1, Math.max(0, n));
}
function NodeSelect({ value, onChange, nodes, placeholder, testid }) {
    return /*#__PURE__*/ _jsxs(Select, {
        value: value,
        onValueChange: onChange,
        children: [
            /*#__PURE__*/ _jsx(SelectTrigger, {
                className: "w-[180px]",
                "data-testid": testid,
                children: /*#__PURE__*/ _jsx(SelectValue, {
                    placeholder: placeholder
                })
            }),
            /*#__PURE__*/ _jsx(SelectContent, {
                children: nodes.map((n)=>/*#__PURE__*/ _jsx(SelectItem, {
                        value: n.id,
                        children: n.displayName
                    }, n.id))
            })
        ]
    });
}
export function GraphPanel({ highlightRef }) {
    const [showSuperseded, setShowSuperseded] = useState(false);
    const { data, isLoading } = useEntityGraph(showSuperseded);
    const upsertNode = useUpsertEntityNode();
    const upsertEdge = useUpsertEntityEdge();
    const overrideEdge = useOverrideEntityEdge();
    const mergeNodes = useMergeEntityNodes();
    const unmergeNodes = useUnmergeEntityNodes();
    const deleteNode = useDeleteEntityNode();
    const deleteEdge = useDeleteEntityEdge();
    const nodes = useMemo(()=>data?.nodes ?? [], [
        data
    ]);
    const edges = useMemo(()=>data?.edges ?? [], [
        data
    ]);
    const nodeName = useMemo(()=>{
        const m = new Map(nodes.map((n)=>[
                n.id,
                n.displayName
            ]));
        return (id)=>m.get(id) ?? id;
    }, [
        nodes
    ]);
    // Selection (re-derived live from the latest data so a refetch never leaves a
    // stale row pinned in the action panels).
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [selectedEdgeId, setSelectedEdgeId] = useState(null);
    const selectedNode = nodes.find((n)=>n.id === selectedNodeId) ?? null;
    const selectedEdge = edges.find((e)=>e.id === selectedEdgeId) ?? null;
    // Transient per-selection state.
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [overriding, setOverriding] = useState(false);
    const [overrideRationale, setOverrideRationale] = useState('');
    const [orgChartRoot, setOrgChartRoot] = useState(null);
    const orgChart = useGovOrgChart(orgChartRoot);
    function selectNode(n) {
        setSelectedNodeId(n?.id ?? null);
        setSelectedEdgeId(null);
        setConfirmDelete(false);
        if (!n || n.nodeType !== 'gov_org') setOrgChartRoot(null);
    }
    function selectEdge(e) {
        setSelectedEdgeId(e?.id ?? null);
        setSelectedNodeId(null);
        setConfirmDelete(false);
        setOverriding(false);
    }
    // Add-entity toolbar form.
    const [addOpen, setAddOpen] = useState(false);
    const [newNodeType, setNewNodeType] = useState('company');
    const [newNodeName, setNewNodeName] = useState('');
    // Edge-create dialog (opened by dragging between two nodes).
    const [pendingEdge, setPendingEdge] = useState(null);
    const [edgeType, setEdgeType] = useState('parent');
    const [edgeSource, setEdgeSource] = useState('');
    const [edgeConfidence, setEdgeConfidence] = useState('0.7');
    // Advanced section.
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [mergePrimary, setMergePrimary] = useState('');
    const [mergeSecondary, setMergeSecondary] = useState('');
    const [mergeRationale, setMergeRationale] = useState('');
    const [lastMerge, setLastMerge] = useState(null);
    const [unmergeRationale, setUnmergeRationale] = useState('');
    const resolutionHistory = useResolutionHistory();
    const [historyUnmergeId, setHistoryUnmergeId] = useState(null);
    const [historyRationale, setHistoryRationale] = useState('');
    function beginEdge(from, to) {
        setPendingEdge({
            from,
            to
        });
        setEdgeType('parent');
        setEdgeSource('');
        setEdgeConfidence('0.7');
    }
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.captureGraph.panelRoot,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                className: "flex flex-row items-center justify-between space-y-0 py-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraCardTitle, {
                                children: "GovCon graph"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mt-1 text-xs text-muted-foreground",
                                children: "Drag from one entity to another to link them. Click an entity or relationship to edit it."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        "data-testid": Testids.captureGraph.addEntityButton,
                        onClick: ()=>setAddOpen((o)=>!o),
                        iconLeft: /*#__PURE__*/ _jsx(Plus, {
                            className: "h-4 w-4"
                        }),
                        children: "Add entity"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3",
                children: [
                    addOpen && /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-end gap-2 rounded-md border bg-muted/20 p-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Type"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: newNodeType,
                                        onValueChange: setNewNodeType,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "w-[160px]",
                                                "data-testid": Testids.captureGraph.addNodeTypeSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: NODE_TYPES.map((t)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: t.value,
                                                        children: t.label
                                                    }, t.value))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex-1 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Name"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        value: newNodeName,
                                        onChange: (e)=>setNewNodeName(e.target.value),
                                        placeholder: "e.g. ACME Corporation",
                                        "data-testid": Testids.captureGraph.addNodeNameInput
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                disabled: !newNodeName.trim() || upsertNode.isPending,
                                "data-testid": Testids.captureGraph.addNodeSubmit,
                                onClick: ()=>upsertNode.mutate({
                                        nodeType: newNodeType,
                                        displayName: newNodeName.trim()
                                    }, {
                                        onSuccess: ()=>{
                                            setNewNodeName('');
                                            setAddOpen(false);
                                        }
                                    }),
                                children: "Add"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                size: "sm",
                                onClick: ()=>setAddOpen(false),
                                children: "Cancel"
                            })
                        ]
                    }),
                    isLoading ? /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Loading graph…"
                    }) : nodes.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                        className: "rounded-md border border-dashed p-8 text-center",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm font-medium",
                                children: "No entities yet"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mx-auto mt-1 max-w-sm text-sm text-muted-foreground",
                                children: "Add the companies, government orgs, and vehicles in this pursuit, then drag between them to map the relationships."
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                className: "mt-3",
                                size: "sm",
                                onClick: ()=>setAddOpen(true),
                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                    className: "h-4 w-4"
                                }),
                                children: "Add the first entity"
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx(GraphCanvas, {
                        nodes: nodes,
                        edges: edges,
                        highlightRef: highlightRef,
                        selectedNodeId: selectedNodeId ?? undefined,
                        onSelectNode: selectNode,
                        onSelectEdge: selectEdge,
                        onConnectEdge: beginEdge
                    }),
                    pendingEdge && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2 rounded-md border bg-muted/30 p-3",
                        "data-testid": Testids.captureGraph.edgeCreateDialog,
                        children: [
                            /*#__PURE__*/ _jsxs("p", {
                                className: "text-xs font-medium text-muted-foreground",
                                children: [
                                    "New relationship:",
                                    ' ',
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-foreground",
                                        children: nodeName(pendingEdge.from)
                                    }),
                                    " →",
                                    ' ',
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-foreground",
                                        children: nodeName(pendingEdge.to)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-end gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        className: "w-[150px]",
                                        value: edgeType,
                                        onChange: (e)=>setEdgeType(e.target.value),
                                        placeholder: "relationship",
                                        "data-testid": Testids.captureGraph.addEdgeTypeInput
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        className: "w-[150px]",
                                        value: edgeSource,
                                        onChange: (e)=>setEdgeSource(e.target.value),
                                        placeholder: "source",
                                        "data-testid": Testids.captureGraph.addEdgeSourceInput
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        className: "w-[90px]",
                                        value: edgeConfidence,
                                        onChange: (e)=>setEdgeConfidence(e.target.value),
                                        placeholder: "0-1",
                                        "data-testid": Testids.captureGraph.addEdgeConfidenceInput
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        disabled: !edgeSource.trim() || upsertEdge.isPending,
                                        "data-testid": Testids.captureGraph.addEdgeSubmit,
                                        onClick: ()=>upsertEdge.mutate({
                                                fromNode: pendingEdge.from,
                                                toNode: pendingEdge.to,
                                                edgeType: edgeType.trim() || 'parent',
                                                source: edgeSource.trim(),
                                                confidence: clamp01(Number.parseFloat(edgeConfidence) || 0)
                                            }, {
                                                onSuccess: ()=>setPendingEdge(null)
                                            }),
                                        children: "Add relationship"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "ghost",
                                        size: "sm",
                                        onClick: ()=>setPendingEdge(null),
                                        children: "Cancel"
                                    })
                                ]
                            })
                        ]
                    }),
                    selectedNode && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2 rounded-md border p-3",
                        "data-testid": Testids.captureGraph.selectedNodePanel,
                        "data-node-id": selectedNode.id,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-sm font-medium",
                                        children: [
                                            selectedNode.displayName,
                                            ' ',
                                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                                variant: "outline",
                                                className: "ml-1",
                                                children: nodeTypeLabel(selectedNode.nodeType)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                                        variant: "ghost",
                                        size: "sm",
                                        onClick: ()=>selectNode(null),
                                        label: "Close",
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "h-4 w-4"
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap gap-2",
                                children: [
                                    selectedNode.nodeType === 'gov_org' && /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        size: "sm",
                                        "data-testid": Testids.captureGraph.orgChartButton,
                                        onClick: ()=>setOrgChartRoot(orgChartRoot === selectedNode.id ? null : selectedNode.id),
                                        children: orgChartRoot === selectedNode.id ? 'Hide org chart' : 'View org chart'
                                    }),
                                    confirmDelete ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "danger",
                                                size: "sm",
                                                disabled: deleteNode.isPending,
                                                "data-testid": Testids.captureGraph.deleteConfirmButton,
                                                onClick: ()=>deleteNode.mutate({
                                                        id: selectedNode.id
                                                    }, {
                                                        onSuccess: ()=>selectNode(null)
                                                    }),
                                                children: "Delete? Its relationships go too"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "ghost",
                                                size: "sm",
                                                onClick: ()=>setConfirmDelete(false),
                                                children: "Cancel"
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "destructive",
                                        size: "sm",
                                        "data-testid": Testids.captureGraph.nodeDeleteButton,
                                        onClick: ()=>setConfirmDelete(true),
                                        children: "Delete"
                                    })
                                ]
                            }),
                            orgChartRoot === selectedNode.id && /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2 rounded-md border bg-muted/20 p-3",
                                "data-testid": Testids.captureGraph.orgChartRoot,
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs font-medium text-muted-foreground",
                                        children: "Government org chart"
                                    }),
                                    orgChart.isLoading ? /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-muted-foreground",
                                        children: "Loading org chart…"
                                    }) : (orgChart.data?.orgs.length ?? 0) === 0 ? /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-muted-foreground",
                                        children: "No office hierarchy under this org yet."
                                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            orgChart.data?.orgs.map((o)=>/*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center justify-between text-sm",
                                                    "data-testid": Testids.captureGraph.orgChartEntry,
                                                    "data-node-id": o.node?.id,
                                                    "data-org-level": o.orgLevel,
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: o.parentNodeId ? 'pl-4' : 'font-medium',
                                                            children: o.node?.displayName
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "text-xs text-muted-foreground",
                                                            children: o.orgLevel || '—'
                                                        })
                                                    ]
                                                }, o.node?.id)),
                                            (orgChart.data?.people.length ?? 0) > 0 && /*#__PURE__*/ _jsxs("div", {
                                                className: "space-y-1 pt-2",
                                                children: [
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "text-xs font-medium text-muted-foreground",
                                                        children: "People"
                                                    }),
                                                    orgChart.data?.people.map((p)=>/*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center justify-between text-sm",
                                                            "data-testid": Testids.captureGraph.orgChartPerson,
                                                            children: [
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    children: p.person?.displayName
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-xs text-muted-foreground",
                                                                    children: p.role
                                                                })
                                                            ]
                                                        }, `${p.person?.id}-${p.role}`))
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    selectedEdge && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2 rounded-md border p-3",
                        "data-testid": Testids.captureGraph.selectedEdgePanel,
                        "data-edge-id": selectedEdge.id,
                        "data-override-status": selectedEdge.overrideStatus,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-sm",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: nodeName(selectedEdge.fromNode)
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "text-muted-foreground",
                                                children: [
                                                    " —",
                                                    selectedEdge.edgeType,
                                                    "→ "
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: nodeName(selectedEdge.toNode)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                                        variant: "ghost",
                                        size: "sm",
                                        onClick: ()=>selectEdge(null),
                                        label: "Close",
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "h-4 w-4"
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-center gap-2 text-xs text-muted-foreground",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            "source: ",
                                            selectedEdge.source || '—'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            "confidence: ",
                                            Math.round(selectedEdge.confidence * 100),
                                            "%"
                                        ]
                                    }),
                                    selectedEdge.overrideStatus === 'user_confirmed' && /*#__PURE__*/ _jsx("span", {
                                        className: "rounded-full bg-green-50 px-2 py-0.5 text-green-700",
                                        children: "canonical"
                                    }),
                                    selectedEdge.overrideStatus === 'superseded' && /*#__PURE__*/ _jsx("span", {
                                        className: "rounded-full bg-gray-100 px-2 py-0.5 text-gray-500",
                                        children: "superseded"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-end gap-2",
                                children: [
                                    selectedEdge.overrideStatus === 'none' && (overriding ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                className: "w-[260px]",
                                                value: overrideRationale,
                                                onChange: (e)=>setOverrideRationale(e.target.value),
                                                placeholder: "e.g. confirmed with the contracting officer",
                                                "data-testid": Testids.captureGraph.overrideRationaleInput
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                disabled: !overrideRationale.trim() || overrideEdge.isPending,
                                                "data-testid": Testids.captureGraph.overrideSubmit,
                                                onClick: ()=>overrideEdge.mutate({
                                                        edgeId: selectedEdge.id,
                                                        rationale: overrideRationale.trim()
                                                    }, {
                                                        onSuccess: ()=>{
                                                            setOverriding(false);
                                                            setOverrideRationale('');
                                                        }
                                                    }),
                                                children: "Confirm"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "ghost",
                                                size: "sm",
                                                onClick: ()=>setOverriding(false),
                                                children: "Cancel"
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        size: "sm",
                                        "data-testid": Testids.captureGraph.edgeOverrideButton,
                                        onClick: ()=>{
                                            setOverriding(true);
                                            setOverrideRationale('');
                                        },
                                        children: "Pin canonical"
                                    })),
                                    confirmDelete ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "danger",
                                                size: "sm",
                                                disabled: deleteEdge.isPending,
                                                "data-testid": Testids.captureGraph.deleteConfirmButton,
                                                onClick: ()=>deleteEdge.mutate({
                                                        id: selectedEdge.id
                                                    }, {
                                                        onSuccess: ()=>selectEdge(null)
                                                    }),
                                                children: "Delete?"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "ghost",
                                                size: "sm",
                                                onClick: ()=>setConfirmDelete(false),
                                                children: "Cancel"
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "destructive",
                                        size: "sm",
                                        "data-testid": Testids.captureGraph.edgeDeleteButton,
                                        onClick: ()=>setConfirmDelete(true),
                                        children: "Delete"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "border-t pt-3",
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                className: "flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground",
                                "data-testid": Testids.captureGraph.advancedToggle,
                                onClick: ()=>setAdvancedOpen((o)=>!o),
                                children: [
                                    advancedOpen ? /*#__PURE__*/ _jsx(ChevronDown, {
                                        className: "h-3.5 w-3.5"
                                    }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "h-3.5 w-3.5"
                                    }),
                                    "Advanced — duplicates & conflicting relationships"
                                ]
                            }),
                            advancedOpen && /*#__PURE__*/ _jsxs("div", {
                                className: "mt-3 space-y-4",
                                children: [
                                    /*#__PURE__*/ _jsxs("label", {
                                        className: "flex items-center gap-2 text-xs text-muted-foreground",
                                        children: [
                                            /*#__PURE__*/ _jsx("input", {
                                                type: "checkbox",
                                                checked: showSuperseded,
                                                onChange: (e)=>setShowSuperseded(e.target.checked),
                                                "data-testid": Testids.captureGraph.showSupersededToggle
                                            }),
                                            "Show superseded relationships"
                                        ]
                                    }),
                                    nodes.length >= 2 && /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs font-medium text-muted-foreground",
                                                children: "Merge duplicate entities"
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex flex-wrap items-end gap-2",
                                                children: [
                                                    /*#__PURE__*/ _jsx(NodeSelect, {
                                                        value: mergePrimary,
                                                        onChange: setMergePrimary,
                                                        nodes: nodes,
                                                        placeholder: "Keep (primary)",
                                                        testid: Testids.captureGraph.mergePrimarySelect
                                                    }),
                                                    /*#__PURE__*/ _jsx(NodeSelect, {
                                                        value: mergeSecondary,
                                                        onChange: setMergeSecondary,
                                                        nodes: nodes,
                                                        placeholder: "Fold in (secondary)",
                                                        testid: Testids.captureGraph.mergeSecondarySelect
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        className: "w-[200px]",
                                                        value: mergeRationale,
                                                        onChange: (e)=>setMergeRationale(e.target.value),
                                                        placeholder: "rationale (e.g. same UEI)",
                                                        "data-testid": Testids.captureGraph.mergeRationaleInput
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "secondary",
                                                        disabled: !mergePrimary || !mergeSecondary || mergePrimary === mergeSecondary || !mergeRationale.trim() || mergeNodes.isPending,
                                                        "data-testid": Testids.captureGraph.mergeSubmit,
                                                        onClick: ()=>{
                                                            const label = `${nodeName(mergeSecondary)} → ${nodeName(mergePrimary)}`;
                                                            mergeNodes.mutate({
                                                                primaryNodeId: mergePrimary,
                                                                secondaryNodeId: mergeSecondary,
                                                                rationale: mergeRationale.trim()
                                                            }, {
                                                                onSuccess: (resp)=>{
                                                                    setMergeSecondary('');
                                                                    setMergeRationale('');
                                                                    if (resp.logEntryId) {
                                                                        setLastMerge({
                                                                            logEntryId: resp.logEntryId,
                                                                            label
                                                                        });
                                                                        setUnmergeRationale('');
                                                                    }
                                                                }
                                                            });
                                                        },
                                                        children: "Merge"
                                                    })
                                                ]
                                            }),
                                            lastMerge && /*#__PURE__*/ _jsxs("div", {
                                                className: "flex flex-wrap items-end gap-2 rounded-md border bg-muted/30 p-3",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        className: "text-xs text-muted-foreground",
                                                        children: [
                                                            "Merged ",
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: "font-medium",
                                                                children: lastMerge.label
                                                            }),
                                                            ". Undo?"
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        className: "w-[200px]",
                                                        value: unmergeRationale,
                                                        onChange: (e)=>setUnmergeRationale(e.target.value),
                                                        placeholder: "rationale (e.g. not the same entity)",
                                                        "data-testid": Testids.captureGraph.unmergeRationaleInput
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "secondary",
                                                        disabled: !unmergeRationale.trim() || unmergeNodes.isPending,
                                                        "data-testid": Testids.captureGraph.undoMergeButton,
                                                        onClick: ()=>unmergeNodes.mutate({
                                                                resolutionLogId: lastMerge.logEntryId,
                                                                rationale: unmergeRationale.trim()
                                                            }, {
                                                                onSuccess: ()=>{
                                                                    setLastMerge(null);
                                                                    setUnmergeRationale('');
                                                                }
                                                            }),
                                                        children: "Undo merge"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "ghost",
                                                        onClick: ()=>setLastMerge(null),
                                                        children: "Dismiss"
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    (resolutionHistory.data?.length ?? 0) > 0 && /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-2",
                                        "data-testid": Testids.captureGraph.resolutionHistoryRoot,
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs font-medium text-muted-foreground",
                                                children: "Resolution history"
                                            }),
                                            resolutionHistory.data?.map((entry)=>/*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm",
                                                    "data-testid": Testids.captureGraph.resolutionHistoryEntry,
                                                    "data-action": entry.action,
                                                    "data-log-id": entry.id,
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            className: "flex flex-col",
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("span", {
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "font-medium capitalize",
                                                                            children: entry.action
                                                                        }),
                                                                        entry.secondaryNodeId && /*#__PURE__*/ _jsxs("span", {
                                                                            className: "text-muted-foreground",
                                                                            children: [
                                                                                ' ',
                                                                                nodeName(entry.secondaryNodeId),
                                                                                " → ",
                                                                                nodeName(entry.primaryNodeId)
                                                                            ]
                                                                        })
                                                                    ]
                                                                }),
                                                                entry.rationale && /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-xs text-muted-foreground",
                                                                    children: entry.rationale
                                                                })
                                                            ]
                                                        }),
                                                        entry.action === 'merge' && historyUnmergeId !== entry.id && /*#__PURE__*/ _jsx(SpecraButton, {
                                                            variant: "secondary",
                                                            size: "sm",
                                                            "data-testid": Testids.captureGraph.resolutionHistoryUnmergeButton,
                                                            onClick: ()=>{
                                                                setHistoryUnmergeId(entry.id);
                                                                setHistoryRationale('');
                                                            },
                                                            children: "Unmerge"
                                                        })
                                                    ]
                                                }, entry.id)),
                                            historyUnmergeId && /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-end gap-2 rounded-md border bg-muted/30 p-3",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex-1 space-y-1",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("label", {
                                                                className: "text-xs font-medium",
                                                                children: "Why reverse this merge?"
                                                            }),
                                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                                value: historyRationale,
                                                                onChange: (e)=>setHistoryRationale(e.target.value),
                                                                placeholder: "e.g. not the same entity after all",
                                                                "data-testid": Testids.captureGraph.resolutionHistoryRationaleInput
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        disabled: !historyRationale.trim() || unmergeNodes.isPending,
                                                        "data-testid": Testids.captureGraph.resolutionHistoryUnmergeConfirm,
                                                        onClick: ()=>unmergeNodes.mutate({
                                                                resolutionLogId: historyUnmergeId,
                                                                rationale: historyRationale.trim()
                                                            }, {
                                                                onSuccess: ()=>setHistoryUnmergeId(null)
                                                            }),
                                                        children: "Confirm"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "ghost",
                                                        onClick: ()=>setHistoryUnmergeId(null),
                                                        children: "Cancel"
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
